import { useState, useEffect, useMemo, useRef } from 'react';
import { orderBy } from 'lodash';
import { QUERY_NAMES } from 'react/services/AudienceServiceHelper';
import { getSegmentInsightsData } from 'react/services/AudienceInsightsService';
import { convertStringToFloat } from 'react/utils/NumberUtils';
import { SORT_TYPE_TO_TOPIC_KEY } from '../constants';
import { throwForInvalidDataResponse, transformTopicsData } from '../utils/audienceInsightsUtils';

/**
 * Hook for managing topics data fetching and sorting
 */
const useTopicsData = ({
  segment,
  audienceChannel,
  targetRatioObj,
  sortType,
  selectedTab,
  tabsNames,
  infraUtil,
  expelUserIfMissingData,
}) => {
  const [topicsData, setTopicsData] = useState(null);
  const [topic, setTopic] = useState(null);
  const [topicsPromise, setTopicsPromise] = useState(null);

  // Token to track the latest request and avoid race conditions
  const latestRequestTokenRef = useRef(0);

  // Fetch topics data
  useEffect(() => {
    if ([tabsNames?.categories, tabsNames?.purchaseAffinities, 'demographics'].includes(selectedTab?.value)) {
      return;
    }

    const token = ++latestRequestTokenRef.current;

    const promise = getSegmentInsightsData(segment, QUERY_NAMES.topics, audienceChannel, targetRatioObj)
      .then(throwForInvalidDataResponse)
      .catch(expelUserIfMissingData)
      .then((data) => {
        if (token !== latestRequestTokenRef.current) return null;
        return data.insights || [];
      })
      .then((insights) => {
        if (token !== latestRequestTokenRef.current || !insights) return null;
        return transformTopicsData(insights);
      })
      .then((data) => {
        if (token !== latestRequestTokenRef.current || !data) return null;
        return orderBy(data, SORT_TYPE_TO_TOPIC_KEY[sortType], 'desc');
      });

    setTopicsPromise(promise);

    promise.then((topics) => {
      if (token !== latestRequestTokenRef.current || !topics) return;

      setTopicsData(topics);
      // Set initial topic if not already set
      if (!topic && topics[0]) {
        setTopic(topics[0]);
      }
    });
  }, [segment, audienceChannel, targetRatioObj, sortType, selectedTab, tabsNames, expelUserIfMissingData]);

  // Derive sorted and normalized topics data
  const sortedTopicsData = useMemo(() => {
    if (!topicsData || !infraUtil) return topicsData;

    const updatedTopics = topicsData.map((i) => ({
      ...i,
      percents: sortType === 'portion' ? i.percentValue : i.value,
    }));
    const normalized = infraUtil.normalize(updatedTopics, 'percents', 'percents');
    const compareFunction = (obj) => convertStringToFloat(obj[SORT_TYPE_TO_TOPIC_KEY[sortType]]);
    return orderBy(normalized, compareFunction, 'desc');
  }, [topicsData, infraUtil, sortType]);

  return {
    topicsData,
    sortedTopicsData,
    topic,
    setTopic,
    topicsPromise,
  };
};

export default useTopicsData;
